import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Languages, Building, User, ShieldCheck, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const { lang, switchLang } = useLang();
  const [company, setCompany] = useState({
    name: "", tax_id: "", address: "", city: "", state: "", zip_code: "",
    phone: "", fax: "", email: "", website: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/setup/company-profile")
      .then((r) => {
        const d = r.data || {};
        setCompany((c) => ({
          ...c,
          name: d.name || "", tax_id: d.tax_id || d.ein || "",
          address: d.address || "", city: d.city || "", state: d.state || "", zip_code: d.zip_code || "",
          phone: d.phone || "", fax: d.fax || "", email: d.email || "", website: d.website || "",
        }));
      })
      .finally(() => setLoading(false));
  }, []);

  const saveCompany = async () => {
    setSaving(true);
    try {
      await api.patch("/setup/company-profile", company);
      toast.success("Dados da empresa salvos — aparecem no cabeçalho do próximo Invoice / Apresentação");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Account</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">Settings</h1>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
          <User className="w-3.5 h-3.5" /> Profile
        </div>
        <Row label="Name" value={user?.name} />
        <Row label="Email" value={user?.email} mono />
        <Row label="Role" value={user?.role?.toUpperCase()} />
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 space-y-3" data-testid="company-info-form">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
            <Building className="w-3.5 h-3.5" /> Company Info · aparece nos PDFs
          </div>
          <Button size="sm" onClick={saveCompany} disabled={saving || loading} className="bg-blue-600 hover:bg-blue-500" data-testid="save-company-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Salvar
          </Button>
        </div>
        {loading ? (
          <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-500" /></div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <F label="Company Name" value={company.name} onChange={(v) => setCompany({ ...company, name: v })} span={2} />
            <F label="Tax ID / EIN" value={company.tax_id} onChange={(v) => setCompany({ ...company, tax_id: v })} />
            <F label="Email" value={company.email} onChange={(v) => setCompany({ ...company, email: v })} />
            <F label="Address" value={company.address} onChange={(v) => setCompany({ ...company, address: v })} span={2} placeholder="62 Chelsea St" />
            <F label="City" value={company.city} onChange={(v) => setCompany({ ...company, city: v })} />
            <F label="State / ZIP" value={company.state} onChange={(v) => setCompany({ ...company, state: v })} />
            <F label="Phone" value={company.phone} onChange={(v) => setCompany({ ...company, phone: v })} />
            <F label="Fax" value={company.fax} onChange={(v) => setCompany({ ...company, fax: v })} />
            <F label="Website" value={company.website} onChange={(v) => setCompany({ ...company, website: v })} span={2} placeholder="https://www.trueadascalibration.com" />
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-4">
          <Languages className="w-3.5 h-3.5" /> Language
        </div>
        <div className="flex gap-2">
          {[{ k: "en", l: "English" }, { k: "pt", l: "Português" }].map((l) => (
            <Button
              key={l.k}
              data-testid={`settings-lang-${l.k}`}
              variant={lang === l.k ? "default" : "outline"}
              className={lang === l.k ? "bg-blue-600 hover:bg-blue-500" : "border-slate-700"}
              onClick={() => switchLang(l.k)}
            >{l.l}</Button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-4">
          <ShieldCheck className="w-3.5 h-3.5" /> Integrations
        </div>
        <div className="space-y-2">
          <Integration name="OpenAI GPT-4o" status="connected" detail="via Emergent LLM Key" />
          <Integration name="Stripe" status="test mode" detail="sk_test_* configured (mock fallback)" />
          <Integration name="Stripe Webhook" status="planned" detail="Add STRIPE_WEBHOOK_SECRET in .env" />
          <Integration name="Twilio SMS" status="mocked" detail="Logging to DB only" />
          <Integration name="Resend Email" status="mocked" detail="Add RESEND_API_KEY in .env for real sending" />
          <Integration name="OneSignal Push" status="mocked" detail="Add ONESIGNAL_APP_ID + ONESIGNAL_API_KEY" />
          <Integration name="Rate Limiting" status="connected" detail="5 submissions/min · 10 uploads/min" />
          <Integration name="CCC ONE" status="planned" detail="V2 integration" />
          <Integration name="Mitchell" status="planned" detail="V2 integration" />
          <Integration name="QuickBooks" status="planned" detail="V2 integration" />
        </div>
      </div>

      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6" data-testid="maintenance-panel">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300 mb-4">
          <ShieldCheck className="w-3.5 h-3.5" /> Manutenção · Admin
        </div>
        <div className="space-y-3">
          <MaintenanceButton
            label="Backfill customer_name em invoices antigas"
            help="Preenche customer_name/email/phone/address em invoices INS/dual antigas usando o job vinculado. Idempotente."
            endpoint="/admin/maintenance/backfill-customer-name"
            testid="btn-backfill-customer"
            successFmt={(d) => `${d.updated} invoices atualizadas · ${d.scanned} varridas · ${d.skipped_no_job} sem job`}
          />
          <MaintenanceButton
            label="Migrar arquivos legacy → Object Storage"
            help="Copia tudo em /app/backend/uploads/* pro storage gerenciado e apaga o arquivo local. Rode dry_run primeiro pra preview."
            endpoint="/admin/maintenance/migrate-legacy-uploads"
            testid="btn-migrate-legacy"
            supportsDryRun
            successFmt={(d) => `${d.moved_count} arquivos migrados · ${d.errors_count} erros${d.dry_run ? " (DRY RUN)" : ""}`}
          />
          <MaintenanceButton
            label="Fechar jobs com todas invoices pagas"
            help="Retro-fecha qualquer Job cujas invoices já foram todas pagas ou canceladas. Cobre 'esquecimentos manuais' antes do auto-close automático. Idempotente."
            endpoint="/admin/maintenance/close-fully-paid-jobs"
            testid="btn-close-fully-paid-jobs"
            supportsDryRun
            successFmt={(d) => `${d.closed_count} jobs fechados · ${d.scanned_open_jobs} varridos${d.dry_run ? " (DRY RUN)" : ""}`}
          />
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-4">
          <Building className="w-3.5 h-3.5" /> Documentation
        </div>
        <a
          data-testid="manual-link"
          href="/manual.md"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-slate-800/60 hover:border-blue-500/40 transition-colors group"
        >
          <div>
            <div className="text-sm font-medium">Manual do Usuário (Português) · com fotos</div>
            <div className="text-xs text-slate-500 mt-0.5">Treinamento completo · 20 seções · 19 capturas de tela · /manual.md</div>
          </div>
          <span className="text-xs uppercase tracking-wider font-bold text-blue-400 group-hover:translate-x-1 transition-transform">Open →</span>
        </a>
        <a
          data-testid="manual-pdf-link"
          href="/manual.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-slate-800/60 hover:border-emerald-500/40 transition-colors group"
        >
          <div>
            <div className="text-sm font-medium">Manual PDF · Imprimível</div>
            <div className="text-xs text-slate-500 mt-0.5">Versão para webinar e treinamento offline · ~2.3 MB</div>
          </div>
          <span className="text-xs uppercase tracking-wider font-bold text-emerald-400 group-hover:translate-x-1 transition-transform">Download ↓</span>
        </a>
      </div>
    </div>
  );
}

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between border-b border-slate-800/40 pb-3 last:border-0 last:pb-0">
    <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</span>
    <span className={`${mono ? "font-mono text-blue-300" : ""} text-sm`}>{value || "—"}</span>
  </div>
);

const F = ({ label, value, onChange, span, placeholder }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">{label}</Label>
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 bg-[#030712] border-slate-800" />
  </div>
);

const Integration = ({ name, status, detail }) => {
  const color = status === "connected" ? "text-emerald-400" : status === "test mode" ? "text-amber-400" : status === "mocked" ? "text-cyan-400" : "text-slate-500";
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-slate-800/60">
      <div>
        <div className="text-sm">{name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{detail}</div>
      </div>
      <span className={`text-xs uppercase tracking-wider font-bold ${color}`}>{status}</span>
    </div>
  );
};


function MaintenanceButton({ label, help, endpoint, testid, supportsDryRun, successFmt }) {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const run = async (dry) => {
    if (!dry && !window.confirm(`Executar: ${label}?\n\nEsta ação percorre o banco/disco e pode demorar alguns segundos.`)) return;
    setBusy(true); setResult(null);
    try {
      const url = supportsDryRun && dry ? `${endpoint}?dry_run=true` : endpoint;
      const r = await api.post(url);
      const msg = successFmt ? successFmt(r.data) : "OK";
      setResult({ ok: true, msg, data: r.data });
      toast.success(msg);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha";
      setResult({ ok: false, msg });
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-amber-500/20 bg-slate-900/40 p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-100">{label}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">{help}</div>
          {result && (
            <div className={`text-[11px] mt-1.5 font-mono ${result.ok ? "text-emerald-300" : "text-rose-300"}`} data-testid={`${testid}-result`}>
              {result.ok ? "✓ " : "✗ "}{result.msg}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {supportsDryRun && (
            <button
              onClick={() => run(true)} disabled={busy} data-testid={`${testid}-dry`}
              className="px-3 py-1.5 rounded-md text-[11px] uppercase tracking-wider font-bold border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
            >{busy ? "..." : "Dry Run"}</button>
          )}
          <button
            onClick={() => run(false)} disabled={busy} data-testid={testid}
            className="px-3 py-1.5 rounded-md text-[11px] uppercase tracking-wider font-bold bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50"
          >{busy ? "Executando..." : "Executar"}</button>
        </div>
      </div>
    </div>
  );
}
